import { useState } from "react";
import { FaChevronLeft, FaChevronRight, FaSyncAlt } from "react-icons/fa";
import PageLoading from "src/components/pageLoading";
import { useTheme } from "src/context/theme.context";
import { TaskFailuresTable } from "../components/task-failures-table";
import { useGetTaskFailuresQuery } from "../task-failures.service";
import { TaskFailuresCursor } from "../types/task-failure.entity";

const TaskFailuresPage = () => {
  const [offsets, setOffsets] = useState<(TaskFailuresCursor | undefined)[]>([undefined]);
  const { isDarkMode } = useTheme();
  const offset = offsets[offsets.length - 1];
  const { data, isLoading, isFetching, isError, refetch } = useGetTaskFailuresQuery({ offset });
  const isBusy = isLoading || isFetching;
  const pageNumber = offsets.length;

  const previousPage = () => {
    if (offsets.length === 1) return;
    setOffsets((current) => current.slice(0, -1));
  };

  const nextPage = () => {
    if (!data?.nextOffset) return;
    setOffsets((current) => [...current, data.nextOffset]);
  };

  return (
    <div className="page_container">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className={`mb-1 text-sm font-medium ${isDarkMode ? "text-sky-300" : "text-sky-700"}`}>Technical reports</p>
          <h1 className={`text-2xl font-semibold ${isDarkMode ? "text-slate-50" : "text-slate-900"}`}>Task Failures</h1>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isBusy}
          title="Refresh task failures"
          aria-label="Refresh task failures"
          className={`inline-flex h-10 w-10 items-center justify-center self-start rounded border transition-colors disabled:cursor-not-allowed disabled:opacity-50 lg:self-auto ${isDarkMode ? "border-slate-600 text-slate-200 hover:border-sky-400 hover:bg-sky-500/15" : "border-slate-200 text-slate-700 hover:border-sky-300 hover:bg-sky-50"}`}
        >
          <FaSyncAlt className={isBusy ? "animate-spin" : ""} />
        </button>
      </div>

      {isLoading ? (
        <PageLoading />
      ) : isError ? (
        <div className={`border p-4 text-sm ${isDarkMode ? "border-rose-500/30 bg-rose-500/10 text-rose-200" : "border-rose-200 bg-rose-50 text-rose-700"}`}>
          Could not load task failures. Please try again.
        </div>
      ) : data?.items.length ? (
        <>
          <div className={`mb-3 flex items-center justify-between text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
            <span>{data.items.length} current failure{data.items.length === 1 ? "" : "s"}</span>
            <span>Page {pageNumber}</span>
          </div>
          <TaskFailuresTable data={data.items} isDarkMode={isDarkMode} />
          <div className="mt-4 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={previousPage}
              disabled={offsets.length === 1 || isBusy}
              title="Previous page"
              aria-label="Previous page"
              className={`inline-flex h-9 w-9 items-center justify-center rounded border transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${isDarkMode ? "border-slate-600 text-slate-200 hover:border-sky-400 hover:bg-sky-500/15" : "border-slate-200 text-slate-600 hover:border-sky-300 hover:bg-sky-50"}`}
            >
              <FaChevronLeft />
            </button>
            <button
              type="button"
              onClick={nextPage}
              disabled={!data.nextOffset || isBusy}
              title="Next page"
              aria-label="Next page"
              className={`inline-flex h-9 w-9 items-center justify-center rounded border transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${isDarkMode ? "border-slate-600 text-slate-200 hover:border-sky-400 hover:bg-sky-500/15" : "border-slate-200 text-slate-600 hover:border-sky-300 hover:bg-sky-50"}`}
            >
              <FaChevronRight />
            </button>
          </div>
        </>
      ) : (
        <div className={`border px-6 py-14 text-center ${isDarkMode ? "border-slate-700 bg-slate-900/50 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
          No current task failures.
        </div>
      )}
    </div>
  );
};

export default TaskFailuresPage;
